import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  toast,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, ShieldAlert, X } from 'lucide-react'
import { useState } from 'react'

import { findModule } from '../modules'
import {
  PLATFORM_ADMIN_ROLE,
  usePlatformApi,
  type VerwaltungsRolle,
  type VerzeichnisGruppe,
  type VerzeichnisPerson,
} from './api'

type Zielart = 'personen' | 'gruppen'

const KEINE_STARTPERSONEN: VerzeichnisPerson[] = []

interface RechteVergabeDialogProps {
  open: boolean
  onClose: () => void
  onDone: () => Promise<void>
  initialPersons?: VerzeichnisPerson[]
}

/**
 * Führt durch die Rechtevergabe. Die Vorschau vor dem Speichern ist bewusst
 * Teil des Ablaufs: Rollen wirken direkt, deshalb soll die Auswahl nicht nur
 * durch einen einzelnen Klick bestätigt werden.
 */
export function RechteVergabeDialog({
  open,
  onClose,
  onDone,
  initialPersons = KEINE_STARTPERSONEN,
}: RechteVergabeDialogProps) {
  const api = usePlatformApi()
  const queryClient = useQueryClient()
  const [schritt, setSchritt] = useState(1)
  const [zielart, setZielart] = useState<Zielart>('personen')
  const [personen, setPersonen] = useState<VerzeichnisPerson[]>(initialPersons)
  const [gruppen, setGruppen] = useState<VerzeichnisGruppe[]>([])
  const [personenSuche, setPersonenSuche] = useState('')
  const [gruppenSuche, setGruppenSuche] = useState('')
  const [rollenAuswahl, setRollenAuswahl] = useState('')
  const [fehlgeschlageneAnzahl, setFehlgeschlageneAnzahl] = useState(0)

  const personenQuery = useQuery({
    queryKey: ['verwaltung', 'rechte-vergabe', 'personen', personenSuche],
    queryFn: ({ signal }) =>
      api.get<{ items: VerzeichnisPerson[] }>('/verzeichnis/personen', {
        query: personenSuche ? { suche: personenSuche } : undefined,
        signal,
      }),
    enabled: open && zielart === 'personen',
  })

  const gruppenQuery = useQuery({
    queryKey: ['verwaltung', 'rechte-vergabe', 'gruppen', gruppenSuche],
    queryFn: ({ signal }) =>
      api.get<VerzeichnisGruppe[]>('/verzeichnis/gruppen', {
        query: gruppenSuche ? { suche: gruppenSuche } : undefined,
        signal,
      }),
    enabled: open && zielart === 'gruppen',
  })

  const rollenQuery = useQuery({
    queryKey: ['verwaltung', 'roles'],
    queryFn: ({ signal }) => api.get<VerwaltungsRolle[]>('/roles', { signal }),
    enabled: open,
  })

  const vergebeneAnzahl = zielart === 'personen' ? personen.length : gruppen.length
  const ausgewählteRolle = rollenQuery.data?.find((rolle) => rolle.name === rollenAuswahl)
  const istKritisch = rollenAuswahl === PLATFORM_ADMIN_ROLE

  const vergeben = useMutation({
    mutationFn: async () => {
      if (zielart === 'personen') {
        const ergebnisse = await Promise.allSettled(
          personen.map(async (person) => {
            const angelegt = await api.post<{ id: string }>(
              '/users/aus-verzeichnis',
              person,
            )
            await api.post(`/users/${encodeURIComponent(angelegt.id)}/roles`, {
              role: rollenAuswahl,
            })
          }),
        )
        return {
          erfolgreich: ergebnisse.filter((ergebnis) => ergebnis.status === 'fulfilled')
            .length,
          gesamt: personen.length,
        }
      }

      const ergebnisse = await Promise.allSettled(
        gruppen.map((gruppe) =>
          api.post(`/gruppen/${encodeURIComponent(gruppe.id)}/rollen`, {
            groupName: gruppe.name,
            role: rollenAuswahl,
          }),
        ),
      )
      return {
        erfolgreich: ergebnisse.filter((ergebnis) => ergebnis.status === 'fulfilled')
          .length,
        gesamt: gruppen.length,
      }
    },
    onMutate: () => setFehlgeschlageneAnzahl(0),
    onSuccess: async ({ erfolgreich, gesamt }) => {
      const bezeichnung = zielart === 'personen' ? 'Personen' : 'Gruppen'

      if (erfolgreich < gesamt) {
        const fehlgeschlagen = gesamt - erfolgreich
        setFehlgeschlageneAnzahl(fehlgeschlagen)
        toast.error(
          `${fehlgeschlagen} von ${gesamt} ${bezeichnung} konnten nicht verarbeitet werden`,
        )
        await queryClient.invalidateQueries({ queryKey: ['verwaltung'] })
        await onDone()
        return
      }

      toast.success(`Rolle für ${erfolgreich} ${bezeichnung} vergeben`)
      await queryClient.invalidateQueries({ queryKey: ['verwaltung'] })
      await onDone()
      onClose()
    },
    onError: () => toast.error('Rolle konnte nicht vergeben werden'),
  })

  function setzeZielart(neu: Zielart) {
    setZielart(neu)
    setSchritt(1)
  }

  function weiter() {
    if (schritt === 1 && vergebeneAnzahl > 0) setSchritt(2)
    if (schritt === 2 && rollenAuswahl) setSchritt(3)
  }

  function zurueck() {
    setSchritt((aktuell) => Math.max(1, aktuell - 1))
  }

  const personVorschlaege = personenQuery.data?.items ?? []
  const gruppenVorschlaege = gruppenQuery.data ?? []

  return (
    <Dialog open={open} onOpenChange={(offen) => !offen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Rechte vergeben</DialogTitle>
          <DialogDescription>
            {schritt === 1 &&
              'Wählen Sie eine oder mehrere Personen beziehungsweise Gruppen aus.'}
            {schritt === 2 && 'Wählen Sie die Rolle und prüfen Sie ihre Bedeutung.'}
            {schritt === 3 &&
              'Prüfen Sie die Zusammenfassung, bevor die Änderung wirksam wird.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 text-sm" aria-label="Fortschritt">
          {[
            ['1', 'Ziele'],
            ['2', 'Rolle'],
            ['3', 'Prüfen'],
          ].map(([nummer, titel]) => (
            <div key={nummer} className="flex items-center gap-2">
              <span
                className={`flex size-7 items-center justify-center rounded-full border ${
                  schritt >= Number(nummer)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'text-muted-foreground'
                }`}
              >
                {nummer}
              </span>
              <span
                className={
                  schritt >= Number(nummer) ? 'font-medium' : 'text-muted-foreground'
                }
              >
                {titel}
              </span>
              {nummer !== '3' && <span className="text-muted-foreground">/</span>}
            </div>
          ))}
        </div>

        {schritt === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-1">
              <Button
                type="button"
                variant={zielart === 'personen' ? 'default' : 'ghost'}
                onClick={() => setzeZielart('personen')}
              >
                Personen
              </Button>
              <Button
                type="button"
                variant={zielart === 'gruppen' ? 'default' : 'ghost'}
                onClick={() => setzeZielart('gruppen')}
              >
                Gruppen
              </Button>
            </div>

            <div className="flex gap-2">
              <div className="min-w-0 flex-1">
                {zielart === 'personen' ? (
                  <Combobox<VerzeichnisPerson>
                    items={personVorschlaege.filter(
                      (person) =>
                        !personen.some(
                          (auswahl) => auswahl.externalId === person.externalId,
                        ),
                    )}
                    value={null}
                    onChange={(person) => {
                      setPersonen((aktuell) => [...aktuell, person])
                      setPersonenSuche('')
                    }}
                    onSearch={setPersonenSuche}
                    getKey={(person) => person.externalId}
                    getLabel={(person) => person.name}
                    getDescription={(person) => person.email}
                    placeholder="Person hinzufügen"
                    emptyText="Keine Person gefunden"
                    loading={personenQuery.isFetching}
                  />
                ) : (
                  <Combobox<VerzeichnisGruppe>
                    items={gruppenVorschlaege.filter(
                      (gruppe) => !gruppen.some((auswahl) => auswahl.id === gruppe.id),
                    )}
                    value={null}
                    onChange={(gruppe) => {
                      setGruppen((aktuell) => [...aktuell, gruppe])
                      setGruppenSuche('')
                    }}
                    onSearch={setGruppenSuche}
                    getKey={(gruppe) => gruppe.id}
                    getLabel={(gruppe) => gruppe.name}
                    placeholder="Gruppe hinzufügen"
                    emptyText="Keine Gruppe gefunden"
                    loading={gruppenQuery.isFetching}
                  />
                )}
              </div>
            </div>

            {zielart === 'personen' && (
              <Alert>
                <AlertTitle>Für regelmäßige Änderungen: Gruppen</AlertTitle>
                <AlertDescription>
                  Gruppen sind meist einfacher zu pflegen. Neue Gruppenmitglieder erhalten
                  die verknüpfte Rolle automatisch.
                </AlertDescription>
              </Alert>
            )}

            <AuswahlListe
              titel={
                zielart === 'personen' ? 'Ausgewählte Personen' : 'Ausgewählte Gruppen'
              }
              namen={(zielart === 'personen' ? personen : gruppen).map(
                (ziel) => ziel.name,
              )}
              onRemove={(name) => {
                if (zielart === 'personen') {
                  setPersonen((aktuell) =>
                    aktuell.filter((person) => person.name !== name),
                  )
                } else {
                  setGruppen((aktuell) =>
                    aktuell.filter((gruppe) => gruppe.name !== name),
                  )
                }
              }}
            />
          </div>
        )}

        {schritt === 2 && (
          <div className="space-y-4">
            <Select value={rollenAuswahl} onValueChange={setRollenAuswahl}>
              <SelectTrigger aria-label="Rolle" className="w-full">
                <SelectValue placeholder="Rolle auswählen" />
              </SelectTrigger>
              <SelectContent>
                {(rollenQuery.data ?? []).map((rolle) => (
                  <SelectItem key={rolle.name} value={rolle.name}>
                    {rolle.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {ausgewählteRolle && (
              <div className="rounded-lg border bg-muted/40 p-4">
                <p className="font-medium">
                  {findModule(ausgewählteRolle.moduleId)?.title ??
                    ausgewählteRolle.moduleId}
                </p>
                <p className="text-sm text-muted-foreground">
                  {ausgewählteRolle.description}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Technischer Rollenname: {ausgewählteRolle.name}
                </p>
              </div>
            )}

            {istKritisch && (
              <Alert variant="destructive">
                <ShieldAlert aria-hidden="true" />
                <AlertTitle>Besonders weitreichende Rolle</AlertTitle>
                <AlertDescription>
                  Diese Rolle erlaubt die Verwaltung der gesamten Plattform und die
                  Vergabe weiterer Rechte. Prüfen Sie die Auswahl besonders sorgfältig.
                </AlertDescription>
              </Alert>
            )}

            {fehlgeschlageneAnzahl > 0 && (
              <Alert variant="destructive">
                <ShieldAlert aria-hidden="true" />
                <AlertTitle>Nicht alle Zuweisungen waren erfolgreich</AlertTitle>
                <AlertDescription>
                  {fehlgeschlageneAnzahl}{' '}
                  {zielart === 'personen' ? 'Personen' : 'Gruppen'} wurden nicht
                  verarbeitet. Prüfen Sie die Auswahl und versuchen Sie es erneut. Bereits
                  erfolgreiche Zuweisungen werden nicht doppelt angelegt.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {schritt === 3 && (
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Diese Rolle wird vergeben</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge tone="info">{ausgewählteRolle?.name}</StatusBadge>
                <span className="font-medium">
                  {ausgewählteRolle
                    ? (findModule(ausgewählteRolle.moduleId)?.title ??
                      ausgewählteRolle.moduleId)
                    : ''}
                </span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {ausgewählteRolle?.description}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">
                <span>Ziele ({vergebeneAnzahl})</span>
                <span> · {zielart === 'personen' ? 'Personen' : 'Gruppen'}</span>
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(zielart === 'personen' ? personen : gruppen).map((ziel) => (
                  <StatusBadge key={`${zielart}:${ziel.name}`} tone="neutral">
                    {ziel.name}
                  </StatusBadge>
                ))}
              </div>
              {zielart === 'gruppen' && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Auch zukünftige Mitglieder dieser Gruppen erhalten die Rolle
                  automatisch.
                </p>
              )}
            </div>
            {istKritisch && (
              <Alert variant="destructive">
                <ShieldAlert aria-hidden="true" />
                <AlertTitle>Letzte Prüfung vor dem Speichern</AlertTitle>
                <AlertDescription>
                  Sie vergeben Administrationsrechte für {vergebeneAnzahl}{' '}
                  {zielart === 'personen' ? 'Personen' : 'Gruppen'}.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={vergeben.isPending}>
            Abbrechen
          </Button>
          {schritt > 1 && (
            <Button variant="ghost" onClick={zurueck} disabled={vergeben.isPending}>
              <ArrowLeft aria-hidden="true" /> Zurück
            </Button>
          )}
          {schritt < 3 ? (
            <Button
              onClick={weiter}
              disabled={schritt === 1 ? vergebeneAnzahl === 0 : !rollenAuswahl}
            >
              Weiter <ArrowRight aria-hidden="true" />
            </Button>
          ) : (
            <Button onClick={() => vergeben.mutate()} disabled={vergeben.isPending}>
              {vergeben.isPending ? 'Wird vergeben …' : 'Rechte vergeben'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AuswahlListe({
  titel,
  namen,
  onRemove,
}: {
  titel: string
  namen: string[]
  onRemove: (name: string) => void
}) {
  if (namen.length === 0) {
    return <p className="text-sm text-muted-foreground">Noch keine Auswahl getroffen.</p>
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        {titel} ({namen.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {namen.map((name) => (
          <StatusBadge key={name} tone="neutral" className="gap-1 pr-1">
            {name}
            <button
              type="button"
              className="rounded-sm hover:text-foreground"
              aria-label={`${name} entfernen`}
              onClick={() => onRemove(name)}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </StatusBadge>
        ))}
      </div>
    </div>
  )
}
